import { BooleanEditor, ComboboxEditor, EditorType, EditorTypeRegistry, EditorUtils, EnumEditor, Fluent, PropertyItem, Widget, extend, isInstanceOfType, isPromiseLike, localText, toId } from "@serenity-is/corelib";
import { Editor, EditorOptions, Grid, Position, ValidationResult } from "@serenity-is/sleekgrid";
import { GridEditController } from "./GridEditController";

export class GridEditWrapper implements Editor {
    declare private args: EditorOptions;
    declare private editor: Widget<any>;
    declare private defaultValue: any;

    constructor(args: EditorOptions) {
        this.args = args;

        var controller = (args.grid as Grid).getOptions().editorFactory as GridEditController<any>;
        var propertyItem: PropertyItem = args.column.editableProperty ?? args.column.sourceItem ?? {} as PropertyItem;

        var editorParams = extend({} as PropertyItem, propertyItem.editorParams);
        var ctrlEditorParams = controller?.options?.editorParams;
        if (ctrlEditorParams != null) {
            if (typeof ctrlEditorParams === "function")
                ctrlEditorParams = ctrlEditorParams({ column: args.column, item: args.item, slickArgs: args });
            editorParams = extend(editorParams, ctrlEditorParams);
        }

        var editorType: (EditorType | PromiseLike<EditorType>) = controller?.options?.editorType?.({
            column: args.column, 
            item: args.item, 
            slickArgs: args
        });

        if (editorType == null) {
            const ed = propertyItem.editorType;
            editorType = (isPromiseLike(ed) || typeof ed === "function") ? ed : (EditorTypeRegistry.getOrLoad(ed ?? 'String'));
        }

        if (isPromiseLike(editorType))
            throw "Lazy editor type loading is not supported by the GridEditWrapper."

        this.editor = Widget.create({
            container: args.container,
            element: el => el.addClass('s-cell-editor'),
            type: editorType,
            options: editorParams
        });

        var maxLength = editorParams.maxLength ?? editorParams.maxlength ?? propertyItem?.maxLength;
        if (maxLength != null) {
            if (Fluent.isInputLike(this.editor.domNode)) {
                if (maxLength > 0) {
                    this.editor.domNode?.setAttribute?.('maxlength', maxLength);
                }
                else {
                    this.editor.domNode?.removeAttribute?.('maxlength');
                }
            }
        }

        this.editor.element.focus();
        this.editor.domNode["select"]?.();

        controller?.options?.editorInit?.({
            column: args.column, editor: this.editor, item: args.item, slickArgs: args
        });
    }

    focus(): void {
        this.editor.element.focus();
    }

    isValueChanged(): boolean {
        return JSON.stringify(this.serializeValue()) !=
            JSON.stringify(this.defaultValue);
    }

    keyCaptureList?: number[];

    loadValue(source: any): void {
        const propertyItem = this.args.column.editableProperty ?? this.args.column.sourceItem;
        EditorUtils.loadValue(this.editor, propertyItem, source);
        this.defaultValue = this.serializeValue();
    }

    serializeValue() {
        const propertyItem = this.args.column.editableProperty ?? this.args.column.sourceItem;
        const target = Object.create(null);
        EditorUtils.saveValue(this.editor, propertyItem, target);

        if (isInstanceOfType(this.editor, EnumEditor)) {
            var value = target[propertyItem.name];
            if (typeof value === "string" && !isNaN(value as any)) {
                target[propertyItem.name] = toId(value);
            }
        }

        if (propertyItem.name !== this.args.column.field &&
            isInstanceOfType(this.editor, ComboboxEditor))
            target[this.args.column.field] = (this.editor as ComboboxEditor<any, any>).text;

        return target;
    }

    applyValue(target: any, state: any): void {
        for (var k of Object.keys(state))
            target[k] = state[k];
    }

    position?(pos: Position): void {
    }

    preClick?(): void {
        if (this.args.column.sourceItem?.editorType == "Boolean")
            (this.editor as BooleanEditor).value = !(this.editor as BooleanEditor).value;
    }

    hide?(): void {
    }

    show?(): void {
    }

    validate?(): ValidationResult {
        const propertyItem = this.args.column.editableProperty ?? this.args.column.sourceItem;
        const value = EditorUtils.getValue(this.editor);
        if (propertyItem.required &&
            (value == null || (typeof value === "string" && !value.trim().length))) {
            return {
                valid: false,
                msg: localText("Validation.Required")
            }
        }

        if (this.args.column.validator != null) {
            var result = this.args.column.validator(value, this.args);
            if (!result.valid)
                return result;
        }

        var controller = (this.args.grid as Grid).getOptions().editorFactory as GridEditController<any>;
        if (controller &&
            controller.options?.validator) {
            var message = controller.options.validator(value, this.args);
            if (message != null)
                return { valid: false, msg: message };
        }

        return { valid: true, msg: null }
    }

    destroy() {
        this.editor?.destroy();
    }
}